import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderKanban, Plus, Search, Edit2, Trash2, X, Loader2,
  Users, Clock, FileText, Bell, ChevronRight, Building2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  active: { label: 'Actief', color: 'bg-green-100 text-green-700 border-green-200' },
  paused: { label: 'Gepauzeerd', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  completed: { label: 'Voltooid', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  archived: { label: 'Gearchiveerd', color: 'bg-slate-100 text-slate-700 border-slate-200' }
};

const PortalProjectsAdmin = () => {
  const { getAuthHeaders } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    status: 'active'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([fetchProjects(), fetchClients()]);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/projects`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      // Error handled silently
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/admin/clients`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      // Error handled silently
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProject) {
        const response = await fetch(`${API_URL}/api/portal/admin/projects/${editingProject.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            name: formData.name,
            description: formData.description,
            status: formData.status
          })
        });
        
        if (response.ok) {
          toast.success('Project bijgewerkt');
          fetchProjects();
          closeModal();
        } else {
          toast.error('Kon project niet bijwerken');
        }
      } else {
        const response = await fetch(`${API_URL}/api/portal/admin/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          toast.success('Project aangemaakt');
          fetchProjects();
          closeModal();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Kon project niet aanmaken');
        }
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Weet je zeker dat je dit project wilt verwijderen? Alle taken en bestanden worden ook verwijderd.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/portal/admin/projects/${projectId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Project verwijderd');
        fetchProjects();
      } else {
        toast.error('Kon project niet verwijderen');
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      client_id: project.client_id,
      status: project.status
    });
    setShowModal(true);
  };

  const openNewModal = () => {
    setEditingProject(null);
    setFormData({
      name: '',
      description: '',
      client_id: clients[0]?.id || '',
      status: 'active'
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProject(null);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || 'Onbekend';
  };

  const getTaskProgress = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const done = tasks.filter(t => t.status === 'done').length;
    return Math.round((done / tasks.length) * 100);
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getClientName(project.client_id).toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Projectenbeheer</h1>
            <p className="text-slate-600">Beheer projecten voor het klantportaal</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Link
              to="/portal-clients-admin"
              className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
            >
              <Users className="w-5 h-5" />
              Klanten
            </Link>
            <button
              onClick={openNewModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              data-testid="add-project-btn"
            >
              <Plus className="w-5 h-5" />
              Nieuw Project
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Zoek op project of klant..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="project-search"
            />
          </div>
        </div>

        {/* Projects List */}
        {filteredProjects.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchQuery ? 'Geen projecten gevonden' : 'Nog geen projecten'}
            </p>
            {clients.length === 0 && !searchQuery && (
              <p className="text-slate-400 text-sm mt-2">
                Maak eerst een klant aan voordat je een project kunt starten
              </p>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow"
                data-testid={`project-card-${project.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Link
                        to={`/portal-admin/projects/${project.id}`}
                        className="text-lg font-semibold text-slate-900 hover:text-blue-600 transition-colors"
                      >
                        {project.name}
                      </Link>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${statusConfig[project.status]?.color}`}>
                        {statusConfig[project.status]?.label}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Building2 className="w-4 h-4" />
                      <span>{getClientName(project.client_id)}</span>
                    </div>
                    
                    {project.description && (
                      <p className="text-slate-500 text-sm mt-2 line-clamp-1">{project.description}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Progress */}
                    <div className="min-w-[100px] hidden sm:block">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-400">Voortgang</span>
                        <span className="text-slate-600 font-medium">{getTaskProgress(project.tasks)}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${getTaskProgress(project.tasks)}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {project.tasks?.length || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {project.files_count || 0}
                      </span>
                      {project.unread_updates > 0 && (
                        <span className="flex items-center gap-1 text-red-500">
                          <Bell className="w-4 h-4" />
                          {project.unread_updates}
                        </span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/portal-admin/projects/${project.id}`}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Beheren"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                      <button
                        onClick={() => openEditModal(project)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Bewerken"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(project.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Verwijderen"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-900">
                    {editingProject ? 'Project Bewerken' : 'Nieuw Project'}
                  </h2>
                  <button onClick={closeModal} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Projectnaam *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="project-name-input"
                    />
                  </div>

                  {!editingProject && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Klant *</label>
                      <select
                        value={formData.client_id}
                        onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                        data-testid="project-client-select"
                      >
                        <option value="">Selecteer een klant</option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>
                            {client.company_name} ({client.contact_name})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Beschrijving</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      data-testid="project-description-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="project-status-select"
                    >
                      <option value="active">Actief</option>
                      <option value="paused">Gepauzeerd</option>
                      <option value="completed">Voltooid</option>
                      <option value="archived">Gearchiveerd</option>
                    </select>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button type="button" onClick={closeModal} className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50">
                      Annuleren
                    </button>
                    <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg" data-testid="project-submit-btn">
                      {editingProject ? 'Opslaan' : 'Aanmaken'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PortalProjectsAdmin;
